import { useRef } from 'react'

import type { TechnicalContentGalleryImage } from './types'

type ImageGalleryStripProps = {
  images: TechnicalContentGalleryImage[]
}

export default function ImageGalleryStrip({ images }: ImageGalleryStripProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const pointerStartXRef = useRef(0)
  const scrollStartXRef = useRef(0)
  const draggingRef = useRef(false)

  if (!images.length) {
    return null
  }

  const startDrag = (clientX: number) => {
    const container = containerRef.current

    if (!container) {
      return
    }

    draggingRef.current = true
    pointerStartXRef.current = clientX
    scrollStartXRef.current = container.scrollLeft
  }

  const moveDrag = (clientX: number) => {
    const container = containerRef.current

    if (!container || !draggingRef.current) {
      return
    }

    const deltaX = clientX - pointerStartXRef.current

    container.scrollLeft = scrollStartXRef.current - deltaX
  }

  const endDrag = () => {
    draggingRef.current = false
  }

  return (
    <div
      ref={containerRef}
      className="no-scrollbar flex w-full cursor-grab gap-2 overflow-x-auto overflow-y-hidden pb-2 active:cursor-grabbing mt-[28px]"
      data-testid="technical-content-image-gallery"
      onPointerDown={(event) => {
        startDrag(event.clientX)
      }}
      onPointerMove={(event) => {
        moveDrag(event.clientX)
      }}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerLeave={endDrag}
    >
      {images.map(({ image, imageAlt }, index) => (
        <div
          key={`${image}-${index}`}
          className="h-[124px] w-[124px] shrink-0 overflow-hidden md:h-[245px] md:w-[245px] md:flex-none md:shrink-0"
          data-testid="technical-content-gallery-item"
        >
          <img
            src={image}
            alt={imageAlt}
            className="h-full w-full object-cover"
            draggable={false}
          />
        </div>
      ))}
    </div>
  )
}
